import React, { useRef } from "react";
import { useState, useEffect, forwardRef, useImperativeHandle } from "react";

import {
    Text,
    View,
    Dimensions,
    Modal,
    Animated,
    TouchableOpacity,
    ScrollView,
    Platform
} from 'react-native';

import SVGView from "../../utils/SvgView";
import AppTheme from "../../utils/Theme";
import Divider from "../../designSystem/Divider/Divider";
import SelectInstitutionType from "../Authentication/Forms/SelectInstituionType";
import AudioChapter from "../Audio/AudioChapter";
import SelectTeacherFunction from "../Authentication/Forms/SelectTeacherFunction";
import { IcClose } from "../../utils/Icons";

interface ActionSheetProps {
    onChange?: (data?: any) => void,
    componentId: string,
    textColor?: string,
    backgroundColor?: string
}

export type ActionSheetType = "READ_MORE" | "INSTITUTION_TYPE" | "AUDIO_CHAPTER" | "TEACHER_FUNCTION" | "";

interface ActionSheetData {
    arrayOfStrings?: string[],
    title?: string,
    urls?: string[],
    chapters?: string[],
    localImage?: string,
    remoteImage?: string,
    color?: string,
    navColor?: string,
    activityId?: string,
    onActivityCompleted?:() => void,
    adapted?: boolean
}

interface ActionSheetContent {
    isVisible: boolean,
    actionSheetType: ActionSheetType,
    data?: ActionSheetData
}

const ActionSheet = forwardRef((props: ActionSheetProps, ref: any) => {

    const appTheme: AppTheme = new AppTheme();

    const { onChange, componentId, textColor, backgroundColor = appTheme.white } = props;

    const bottomSheetMarginBottom = useRef(new Animated.Value(-appTheme.getFullAppHeight())).current;

    const audioChapterRef = useRef(null);

    const hideTheAlert = () => {
        if ((audioChapterRef !== null) && (audioChapterRef.current !== null)) {
            audioChapterRef.current.onCloseRequest();
        }
        Animated.timing(bottomSheetMarginBottom, {
            toValue: -appTheme.getFullAppHeight(),
            duration: 500,
            useNativeDriver: false
        }).start(() => {
            setTimeout(() => {
                setAlertContent({
                    isVisible: false,
                    actionSheetType: ""
                });
            }, 100);
        });
    }

    const [alertContent, setAlertContent] = useState<ActionSheetContent>({
        isVisible: false,
        actionSheetType: ""
    });

    // ComponentDidMount
    useEffect(() => {
        if (alertContent.isVisible === true) {
            if (Platform.OS === "web") {
                document.body.setAttribute("style", "position:fixed;");
            }
            Animated.timing(bottomSheetMarginBottom, {  
                toValue: 0,
                duration: 500,
                useNativeDriver: false
            }).start();
        } else {
            if (Platform.OS === "web") {
                document.body.setAttribute("style", "");
            }

        }
    }, [alertContent.isVisible]);

    useImperativeHandle(ref, () => ({

        showAlert(alert: { actionSheetType: ActionSheetType, data: ActionSheetData }) {
            const { actionSheetType, data } = alert;
            setAlertContent({
                actionSheetType,
                isVisible: true,
                data
            });
        },

        hideAlert() {
            hideTheAlert();
        }
    }));

    const getActionSheetComponent = () => {
        if (alertContent.actionSheetType === "READ_MORE") {
            const textsToDisplay: string[] | undefined = alertContent.data?.arrayOfStrings;
            if (textsToDisplay !== undefined) {
                let paragraphs = [];
                for (const aTextToDisplayIndex in textsToDisplay) {
                    if (Object.prototype.hasOwnProperty.call(textsToDisplay, aTextToDisplayIndex)) {
                        const aText = textsToDisplay[aTextToDisplayIndex];
                        if (paragraphs.length > 0) {
                            paragraphs.push(<Divider key={"divider_" + aTextToDisplayIndex} />);
                        }
                        paragraphs.push(<Text key={"parag_" + aTextToDisplayIndex} style={{ color: textColor, fontFamily: appTheme.primaryFont, fontSize: appTheme.pixelPerfectForFont(9), textAlign: "left" }}>{aText}</Text>)
                    }
                }
                return <View>
                    <View style={[{ width: "100%", paddingVertical: appTheme.pixelPerfect(10), justifyContent: "center", alignItems: "center" }]}>
                        <Text style={[{ paddingHorizontal: appTheme.pixelPerfect(10), fontFamily: appTheme.superHerosFontBold, fontSize: appTheme.pixelPerfectForFont(10), color: textColor, textAlign: "center" }]} >{alertContent.data?.title.toLocaleUpperCase()}</Text>
                    </View>
                    <ScrollView>
                        {paragraphs}
                    </ScrollView>
                </View>
            }
        } else if (alertContent.actionSheetType === "INSTITUTION_TYPE") {
            return <SelectInstitutionType onSelectionDone={onSelectionDone} />
        } else if (alertContent.actionSheetType === "AUDIO_CHAPTER") {
            return <AudioChapter categoryAnalyticsKey={alertContent.data?.categoryAnalyticsKey} isPremium={alertContent.data?.isPremium} activityAnalyticsKey={alertContent.data?.activityAnalyticsKey} ref={audioChapterRef} chaptersImages={alertContent.data?.chaptersImages} adapted={alertContent.data?.adapted} onActivityCompleted={alertContent.data?.onActivityCompleted} activityId={alertContent.data?.activityId} navColor={alertContent.data?.navColor} color={alertContent.data?.color} title={alertContent.data?.title} localImage={alertContent.data?.localImage} remoteUrl={alertContent.data?.remoteImage} urls={alertContent.data?.urls} chapters={alertContent.data?.chapters} onSelectionDone={onSelectionDone} />
        } else if (alertContent.actionSheetType === "TEACHER_FUNCTION") {
            return <SelectTeacherFunction onSelectionDone={onSelectionDone} />
        }
        return <View />
    }

    const onSelectionDone = (data: any) => {
        if (onChange !== undefined) {
            onChange(data);
        }
        hideTheAlert();
    }

    const actionSheetWidth = Platform.OS === "web" ? appTheme.getBlockWidth() : appTheme.getFullAppWidth();
    const contentView: React.JSX.Element = Platform.OS === "web" ? <ScrollView style={{ flex: 1 }}>
        {getActionSheetComponent()}
    </ScrollView> : getActionSheetComponent();
    return (
        <Modal
            transparent={true}
            animationType="fade"
            visible={alertContent.isVisible}
            style={{ width: Dimensions.get("window").width, height: Dimensions.get("window").height }}>
            <View style={{ paddingLeft:appTheme.webNavBarWidth, width: Dimensions.get("window").width, height: Dimensions.get("window").height, justifyContent: "flex-end", alignItems: "center", backgroundColor: appTheme.alertOverlayColor }}>
                <Animated.View style={{ marginBottom: bottomSheetMarginBottom, minHeight: appTheme.pixelPerfect(60), maxHeight: Dimensions.get("window").height - appTheme.pixelPerfect(20) - appTheme.topInsets, width: actionSheetWidth, paddingBottom: Platform.OS === "web" ? 0 : appTheme.pixelPerfect(40) + appTheme.bottomInsets, borderRadius: appTheme.pixelPerfect(10), backgroundColor }}>
                    {contentView}
                    <TouchableOpacity onPress={hideTheAlert} style={{ position: "absolute", top: appTheme.pixelPerfect(10), right: appTheme.pixelPerfect(10), width: appTheme.pixelPerfect(30), height: appTheme.pixelPerfect(30), borderRadius: appTheme.pixelPerfect(15), backgroundColor: textColor, justifyContent: "center", alignItems: "center" }}>
                        <SVGView Component={IcClose} size={appTheme.pixelPerfect(24)} color={backgroundColor} />
                    </TouchableOpacity>
                </Animated.View>
            </View>
        </Modal>
    )
});

export default ActionSheet;